/**
 * Allotment of Shares — Otherwise Than Cash (M32202) 문서별 변수 정의 + 영문 라벨/타입 규칙.
 *
 * ⚠️ 변수명은 실제 docx 템플릿(TMP080~TMP091)의 [[변수]] 에서 추출한 값과 1:1 일치한다
 *    (ORG_DOC 은 Always Encrypted → daemon 경유 추출. 메모리 preset-task-doc-source-and-template-vars 참고).
 *
 * CDD(M32210) 문서-뷰 스택(cddFields.ts)을 미러링하되, CDD 가 "문서 유형별 인스턴스"인 반면
 * OTC 는 "한 task = 12문서 패키지 + 공유 values 맵" 이다. 그래서 문서별 key 집합은
 * docCode(TMP0xx) 기준으로 정의하고, 반복부(allottees / shares)는 flat *_${n} 키로 저장 후
 * mapper 가 배열로 확장한다(CDD shareholders 패턴 동일).
 */

export type OtcFieldType = 'text' | 'date' | 'entity' | 'person' | 'combo' | 'combo-free' | 'address';

/** 반복부(allottee / share class) 최대 행 수. */
export const OTC_ALLOTTEE_MAX = 20;
export const OTC_SHARE_CLASS_MAX = 10;

// ─── 12개 템플릿 docCode ────────────────────────────────────────
export type OtcDocCode =
  | 'TMP080' // Waiver of Preemption Rights
  | 'TMP091' // Authority to Issue Shares (DCR + MWR)
  | 'TMP088' // Pricing for Allotment of Shares (DCR)
  | 'TMP081' // Subscription (Individual) OTC
  | 'TMP082' // Subscription (Corporate) OTC
  | 'TMP089' // Letter of Allotment (OTC)
  | 'TMP090' // Increase of Paid-Up Capital (OTC) (DCR)
  | 'TMP086' // Section 76 Notice (Director)
  | 'TMP085' // Section 76 Notice (Secretary)
  | 'TMP083' // Statement of Shares Allotted Otherwise Than for Cash
  | 'TMP084' // Section 78 Return of Allotment
  | 'TMP087'; // ROC Cover Letter (Section 76)

export interface OtcDocMeta {
  docCode: OtcDocCode;
  label: string;
  stage: number;                 // 1~4 (작성 흐름 단계)
  icon: string;                  // 스텝 라벨 이모지
  only?: 'individual' | 'corporate'; // Stage 2 청약자 유형 분기
  /** 채우기 대상(스칼라) 변수 — progress/검증 기준. 반복/조건/Signers 는 제외. */
  keys: string[];
  /** 문서가 쓰는 반복부 종류. */
  loops?: Array<'allottees' | 'shares'>;
}

// ─── 공통(여러 문서 공유) 스칼라 ──────────────────────────────────
// old_registration_no = 구 등록번호(entity REG_NO_OLD). 값 있을 때만 헤더에 (구번호) 표시.
const COMPANY = ['company_name', 'company_registration_no', 'old_registration_no'];
const COSEC = ['cosec_name', 'cosec_address'];
const DCR = ['constitution_clause_number', 'dcr_date'];

export const OTC_DOCS: Record<OtcDocCode, OtcDocMeta> = {
  // ── Stage 1 — Board Approval & Pricing ──
  TMP080: {
    docCode: 'TMP080', label: 'Waiver of Preemption Rights', stage: 1, icon: '📝',
    keys: ['letter_date', 'company_name', 'total_new_shares'],
    loops: ['allottees'],
  },
  TMP091: {
    docCode: 'TMP091', label: "Authority to Issue Shares (DCR + Members' Written Resolution)", stage: 1, icon: '⚖️',
    keys: [...COMPANY, 'constitution_clause_number', 'dcr_date', 'circulation_date', ...COSEC, 'deadline_date'],
  },
  TMP088: {
    docCode: 'TMP088', label: 'Pricing for Allotment (DCR)', stage: 1, icon: '💲',
    keys: [...COMPANY, 'constitution_clause_number', 'share_price', 'dcr_date'],
  },

  // ── Stage 2 — Subscription & Letter of Allotment ──
  TMP081: {
    docCode: 'TMP081', label: 'Subscription — Individual', stage: 2, icon: '🙍', only: 'individual',
    keys: [
      'letter_date', 'company_name', 'company_address', 'shares_subscribed', 'share_price',
      'subscriber_name', 'subscriber_id', 'subscriber_address', 'subscriber_type', 'subscriber_occupation',
      'subscriber_dob', 'subscriber_nationality', 'subscriber_race',
      'politician_details', 'pep_connection_details', 'govt_position_details',
      'bo_full_name', 'bo_nature', 'bo_place', 'bo_registration_no', 'bo_registered_address', 'bo_business_address',
      'shares_offered', 'shares_accepted',
    ],
  },
  TMP082: {
    docCode: 'TMP082', label: 'Subscription — Corporate', stage: 2, icon: '🏢', only: 'corporate',
    keys: [
      'letter_date', 'company_name', 'company_address', 'shares_subscribed', 'share_price',
      'subscriber_name', 'subscriber_id', 'subscriber_address', 'subscriber_type', 'subscriber_occupation',
      'subscriber_dob', 'subscriber_nationality', 'subscriber_race',
      'politician_details', 'pep_connection_details', 'govt_position_details',
      'bo_full_name', 'bo_nature', 'bo_place', 'bo_registration_no', 'bo_registered_address', 'bo_business_address',
      'shares_offered', 'shares_accepted',
    ],
  },
  TMP089: {
    docCode: 'TMP089', label: 'Letter of Allotment', stage: 2, icon: '✉️',
    keys: [...COMPANY, 'letter_date', ...COSEC, 'capitalized_amount', 'creditor_name', 'total_new_shares', 'share_price'],
    loops: ['allottees'],
  },

  // ── Stage 3 — Allotment Resolution & Capital Increase ──
  TMP090: {
    docCode: 'TMP090', label: 'Increase of Paid-Up Capital (DCR)', stage: 3, icon: '📈',
    keys: [...COMPANY, 'constitution_clause_number', 'paid_up_capital_before', 'paid_up_capital_after', 'total_new_shares', 'share_price', 'dcr_date'],
    loops: ['allottees'],
  },
  TMP086: {
    docCode: 'TMP086', label: 'Section 76 Notice — Director', stage: 3, icon: '📄',
    keys: [...COMPANY, 'allotment_date', 'conditions_text', 'signature_date'],
  },
  TMP085: {
    docCode: 'TMP085', label: 'Section 76 Notice — Secretary', stage: 3, icon: '📄',
    keys: [
      ...COMPANY, 'allotment_date', 'conditions_text',
      'secretary_name', 'secretary_license_no', 'secretary_sspc_no', 'signature_date',
      'lodger_name', 'lodger_nric', 'lodger_address', 'lodger_phone', 'lodger_email',
    ],
  },

  // ── Stage 4 — Statutory Lodgement ──
  TMP083: {
    docCode: 'TMP083', label: 'Statement of Shares Allotted (OTC)', stage: 4, icon: '📊',
    keys: [...COMPANY, 'total_new_shares', 'share_price', 'creditor_name', 'allotment_date', 'capitalized_amount', 'statement_date'],
    loops: ['allottees'],
  },
  TMP084: {
    docCode: 'TMP084', label: 'Section 78 — Return of Allotment', stage: 4, icon: '📋',
    keys: [
      ...COMPANY, 'allotment_date', 'period_start_date', 'period_end_date',
      'total_current_cash', 'total_current_otherwise', 'total_current',
      'total_additional_cash', 'total_accumulated_cash', 'total_additional_otherwise', 'total_accumulated_otherwise',
      'total_additional', 'total_accumulated',
      'director_name', 'director_nric', 'signature_date',
    ],
    loops: ['shares', 'allottees'],
  },
  TMP087: {
    docCode: 'TMP087', label: 'ROC Cover Letter (Section 76)', stage: 4, icon: '🗂️',
    keys: [
      'cosec_name', 'cosec_registration_no', 'cosec_address', 'cosec_phone', 'submission_reference',
      ...COMPANY, 'letter_date', 'tarikh_dokumen', 'event_date',
    ],
  },
};

export const OTC_DOC_CODES = Object.keys(OTC_DOCS) as OtcDocCode[];

/** 4단계 스텝 정의 (스텝퍼 표시용). */
export const OTC_STAGES: Array<{ no: number; en: string }> = [
  { no: 1, en: 'Board Approval & Pricing' },
  { no: 2, en: 'Subscription & Letter of Allotment' },
  { no: 3, en: 'Allotment Resolution & Capital Increase' },
  { no: 4, en: 'Statutory Lodgement' },
];

// ─── 토글(조건 [[if X]]) — 문서별 상태. 값은 boolean. ────────────
export type OtcFlag =
  | 'has_constitution'
  | 'multiple_members'
  | 'is_multiple_director_company' // false → is_sole_director_company
  | 'resolution_basis' // 'constitution' | 'articles' | 'third_schedule' (문자열 flag)
  | 'is_multiple_member'           // false → is_sole_member
  | 'grant_of_rights'              // false → shares
  | 'has_conditions'
  | 'is_politician'
  | 'is_pep_connected'
  | 'holds_govt_position'
  | 'is_trustee'
  | 'clause_2_applicable'
  | 'clause_3_applicable'
  | 'statement_resolution_basis';  // 'dividend' | 'reserve'

// ─── 라벨 ────────────────────────────────────────────────────────
const OVERRIDES: Record<string, string> = {
  company_name: 'Company Name', company_registration_no: 'Company Registration No.', old_registration_no: 'Old Registration No.', company_address: 'Registered Office Address',
  letter_date: 'Letter Date', dcr_date: 'DCR Date', circulation_date: 'Circulation Date', deadline_date: 'Deadline Date',
  allotment_date: 'Allotment Date', signature_date: 'Signature Date', statement_date: 'Statement Date', event_date: 'Event Date',
  tarikh_dokumen: 'Tarikh Dokumen', period_start_date: 'Period Start', period_end_date: 'Period End',
  constitution_clause_number: 'Clause / Article No.',
  cosec_name: 'Company Secretary Name', cosec_address: 'Company Secretary Address', cosec_phone: 'Company Secretary Phone',
  cosec_registration_no: 'Company Secretary Reg. No.',
  share_price: 'Share Price (RM)', total_new_shares: 'Total New Shares', capitalized_amount: 'Capitalized Amount (RM)',
  creditor_name: 'Creditor Name', paid_up_capital_before: 'Paid-Up Capital (Before)', paid_up_capital_after: 'Paid-Up Capital (After)',
  subscriber_name: 'Subscriber Name', subscriber_id: 'NRIC / Registration No.', subscriber_address: 'Subscriber Address',
  subscriber_type: 'Subscriber Type', subscriber_occupation: 'Occupation / Nature of Business', subscriber_dob: 'Date of Birth',
  subscriber_nationality: 'Nationality', subscriber_race: 'Race',
  shares_subscribed: 'No. of Shares Subscribed', shares_offered: 'No. of Shares Offered', shares_accepted: 'No. of Shares Accepted',
  politician_details: 'Politician Details', pep_connection_details: 'PEP Connection Details', govt_position_details: 'Govt Position Details',
  bo_full_name: 'Beneficial Owner Name', bo_nature: 'Nature of Control', bo_place: 'Place', bo_registration_no: 'BO Registration No.',
  bo_registered_address: 'BO Registered Address', bo_business_address: 'BO Business Address',
  secretary_name: 'Secretary Name', secretary_license_no: 'License / Membership No.', secretary_sspc_no: 'SSM PC No.',
  lodger_name: 'Lodger Name', lodger_nric: 'Lodger NRIC', lodger_address: 'Lodger Address', lodger_phone: 'Lodger Phone', lodger_email: 'Lodger Email',
  conditions_text: 'Conditions', submission_reference: 'Submission Reference',
  director_name: 'Director Name', director_nric: 'Director NRIC',
  total_current_cash: 'Current — Cash', total_current_otherwise: 'Current — Otherwise', total_current: 'Current — Total',
  total_additional_cash: 'Additional — Cash', total_accumulated_cash: 'Accumulated — Cash',
  total_additional_otherwise: 'Additional — Otherwise', total_accumulated_otherwise: 'Accumulated — Otherwise',
  total_additional: 'Additional — Total', total_accumulated: 'Accumulated — Total',
  // repeating row fields
  allottee_name: 'Allottee Name', allottee_address: 'Allottee Address', allottee_shares: 'No. of Shares',
  allottee_id_no: 'Allottee NRIC / Reg. No.', allottee_share_type: 'Class of Shares', allottee_share_count: 'No. of Shares',
  share_type: 'Class of Shares', share_details: 'Details', share_number: 'Number Allotted',
  share_price_per_share: 'Price per Share (RM)', share_total_issued: 'Total Issued (RM)',
};

const TOKENS: Record<string, string> = {
  no: 'No.', id: 'ID', nric: 'NRIC', bo: 'BO', dcr: 'DCR', pep: 'PEP', otc: 'OTC', roc: 'ROC', sspc: 'SSM PC',
};

/** allottee_name_3 / share_class_number_2 등 뒤 인덱스 제거한 base key. */
function stripIndex(key: string): string {
  return key.replace(/_\d+$/, '');
}

export function labelize(key: string): string {
  const base = stripIndex(key);
  if (OVERRIDES[base]) return OVERRIDES[base];
  return base.split('_').map((w) => TOKENS[w] || (w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

export function typeOf(key: string): OtcFieldType {
  const b = stripIndex(key);
  //   tarikh_dokumen(ROC 문서일자)은 _date 로 안 끝나지만 날짜 필드 — 달력/직접입력 지원.
  if (/(^|_)(date|dob)$/.test(b) || /_date$/.test(b) || b === 'tarikh_dokumen') return 'date';
  if (b === 'bo_nature' || b === 'bo_place') return 'combo-free'; // 목록 선택 + 직접입력 허용
  if (/nationality$/.test(b)) return 'combo';
  if (/(^|_)race$/.test(b)) return 'combo';
  if (b === 'subscriber_type') return 'combo';
  if (/(^|_)occupation$/.test(b)) return 'combo';
  if (/_address$/.test(b)) return 'address';
  if (/(_name$|creditor_name$|allottee_name$|director_name$|secretary_name$|lodger_name$|bo_full_name$|subscriber_name$)/.test(b)
      && b !== 'company_name' && b !== 'cosec_name') return 'person';
  return 'text';
}

// ─── 주소 그룹: 앵커(*_address) → 형제 컴포넌트(town/state/postcode/country) ──
export interface OtcAddressGroup { line: string; town?: string; state?: string; postcode?: string; country?: string; }
// OTC 템플릿은 town/state/postcode/country 분리 필드가 없으므로 전부 단일-라인 주소.
export const OTC_ADDRESS_LINES = new Set<string>([
  'company_address', 'cosec_address', 'subscriber_address', 'lodger_address',
  'bo_registered_address', 'bo_business_address',
]);
export function addressGroupOf(key: string): OtcAddressGroup | null {
  const base = stripIndex(key);
  if (OTC_ADDRESS_LINES.has(base) || /_address$/.test(base)) return { line: key };
  return null;
}

export type OtcComboSource = 'nationality' | 'occupation' | 'country' | 'race' | 'subscriberType' | 'boNature';
export function comboSourceOf(key: string): OtcComboSource | null {
  const b = stripIndex(key);
  if (b === 'bo_nature') return 'boNature';
  if (b === 'bo_place') return 'country';
  if (/nationality$/.test(b)) return 'nationality';
  if (/(^|_)race$/.test(b)) return 'race';
  if (b === 'subscriber_type') return 'subscriberType';
  if (/(^|_)occupation$/.test(b)) return 'occupation';
  return null;
}

// ─── Subscription 문서를 allottee 별로 렌더/저장 ───────────────────────────
//   Subscription(TMP081/082)은 상단 토글 대신 Stage 1 allottee 마다 1문서씩 생성한다.
//   문서의 base 변수키 → 해당 allottee(index) 의 실제 저장 키로 매핑.
/** 회사 공통(모든 allottee 문서가 공유, index 없음). */
const SUB_COMPANY_SHARED = new Set(['letter_date', 'company_name', 'company_address', 'share_price']);
/** 이름/ID/주소/주식수는 allottee 표를 단일 소스로 재사용. */
const SUB_TO_ALLOTTEE: Record<string, string> = {
  subscriber_name: 'allottee_name',
  subscriber_id: 'allottee_id_no',
  subscriber_address: 'allottee_address',
  shares_subscribed: 'allottee_shares',
  shares_offered: 'allottee_shares',
  shares_accepted: 'allottee_shares',
};
/** base 변수키 + allottee index → state.values 실제 저장 키. */
export function subStoreKey(baseKey: string, index: number | string): string {
  if (SUB_COMPANY_SHARED.has(baseKey)) return baseKey;      // 회사 공통 — index 없음
  const mapped = SUB_TO_ALLOTTEE[baseKey];
  return mapped ? `${mapped}_${index}` : `${baseKey}_${index}`; // 그 외 subscriber_*/bo_*/*_details 는 per-allottee
}

/** allottee 개인/법인 자동 판정. category(B09001 법인 / B09002 개인) 우선, 없으면 ID 로 추론(12자리 숫자 NRIC=개인). */
export function inferAllotteeCategory(categoryCd?: string, idNo?: string): 'individual' | 'corporate' {
  const c = String(categoryCd || '').trim().toUpperCase();
  if (c === 'B09001') return 'corporate';
  if (c === 'B09002') return 'individual';
  const digits = String(idNo || '').replace(/\D/g, '');
  if (digits.length === 12) return 'individual';   // MyKad NRIC
  if (idNo && String(idNo).trim()) return 'corporate'; // 등록번호 형태 → 법인
  return 'individual';
}

/** 날짜/조건상세 등은 선택 입력. progress 계산에서 제외. */
export function isOptional(key: string): boolean {
  const b = stripIndex(key);
  if (/(_date|_dob)$/.test(b) || b === 'tarikh_dokumen') return true;
  if (/^(old_registration_no|conditions_text|politician_details|pep_connection_details|govt_position_details|period_start_date|period_end_date)$/.test(b)) return true;
  if (/^total_/.test(b)) return true;
  return false;
}

// ─── Governing Document(정관 결의 근거) clause 파싱 — M32200/AOD 동일 ───
/** "Article 4(1)" → "4(1)" / "Clause 15" → "15" / 그 외 첫 숫자 token. */
export function parseClauseNo(input: string): string {
  const s = String(input || '').trim();
  if (!s) return '';
  const m1 = s.match(/(?:Article|Clause)\s+([\w()]+)/i);
  if (m1) return m1[1];
  if (!/article|clause|constitution|association|schedule/i.test(s)) return s;
  const m2 = s.match(/\b(\d+[\w()]*)/);
  return m2 ? m2[1] : s;
}

/** 등록 clause 문자열 → resolution_basis 종류. 우선순위 Third Schedule > Articles > Constitution. */
export function clauseBasisOf(text: string): 'constitution' | 'articles' | 'third_schedule' | 'other' {
  const s = String(text || '');
  if (/third\s*schedule/i.test(s)) return 'third_schedule';
  if (/\barticles?\b/i.test(s) || /\bregulation\b/i.test(s) || /\btable\s+a\b/i.test(s) || /\bllp\b/i.test(s)) return 'articles';
  if (/\bconstitution\b/i.test(s) || /\bclause\b/i.test(s)) return 'constitution';
  return 'other';
}
